// src/pages/SocialPage.js — Hub social unifié
// Buddiz + Sessions + Clan dans une seule page à onglets
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, where, onSnapshot, orderBy, serverTimestamp, limit, arrayUnion, arrayRemove
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { computeLevel, ONI_IMG_VERSION} from "../data/demons";
import EmptyState from "../components/EmptyState";
import "./SocialPage.css";

// ── Helpers ───────────────────────────────────────────────────────────
function OniAvatar({ oni, size = 44 }) {
  if (!oni) return <span style={{fontSize: size*0.55}}>👹</span>;
  if (oni.image) return (
    <div style={{width:size, height:size, overflow:"hidden", border:"1px solid var(--border-red)", flexShrink:0}}>
      <img src={`${oni.image}?v=${ONI_IMG_VERSION}`} alt={oni.name} style={{width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top", filter:"saturate(0.85)"}}/>
    </div>
  );
  return <span style={{fontSize: size*0.55}}>{oni.emoji || "👹"}</span>;
}

function timeAgo(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)    return "à l'instant";
  if (diff < 3600)  return `${Math.floor(diff/60)}min`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h`;
  return d.toLocaleDateString("fr-FR", {day:"numeric", month:"short"});
}

// ══════════════════════════════════════════════════════════════════════
// BUDDIZ TAB
// ══════════════════════════════════════════════════════════════════════
function BuddizTab({ user, profile }) {
  const [buddies,  setBuddies]  = useState([]);
  const [requests, setRequests] = useState([]);
  const [search,   setSearch]   = useState("");
  const [results,  setResults]  = useState([]);
  const [uidInput, setUidInput] = useState("");
  const [msg,      setMsg]      = useState("");
  const [searching,setSearching]= useState(false);
  const [view,     setView]     = useState("list"); // list | add
  const oni = profile?.demon?.oni;

  useEffect(() => {
    const q = query(collection(db,"buddiz"), where("users","array-contains",user.uid));
    return onSnapshot(q, async snap => {
      const pairs = snap.docs.map(d => ({id:d.id,...d.data()}));
      const profiles = await Promise.all(pairs.map(async p => {
        const otherUid = p.users.find(u => u !== user.uid);
        if (!otherUid) return null;
        const snap2 = await getDoc(doc(db,"users",otherUid));
        return snap2.exists() ? {...snap2.data(), pairId:p.id} : null;
      }));
      setBuddies(profiles.filter(Boolean));
    });
  }, [user.uid]);

  useEffect(() => {
    const q = query(collection(db,"buddy_requests"), where("toUid","==",user.uid), where("status","==","pending"));
    return onSnapshot(q, snap => setRequests(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [user.uid]);

  const doSearch = async () => {
    if (search.trim().length < 2) return;
    setSearching(true);
    const snap = await getDocs(query(collection(db,"users"), limit(20)));
    setResults(snap.docs.map(d=>({uid:d.id,...d.data()}))
      .filter(u => u.uid !== user.uid && !buddies.some(b=>b.uid===u.uid)
        && (u.displayName||"").toLowerCase().includes(search.toLowerCase())));
    setSearching(false);
  };

  const sendRequest = async (target, targetUid) => {
    if (buddies.some(b=>b.uid===targetUid)) { setMsg("Déjà dans tes Buddiz."); return; }
    await addDoc(collection(db,"buddy_requests"), {
      fromUid:user.uid, fromName:profile?.displayName||"Oni",
      fromEmoji:oni?.emoji||"👹", fromOni:oni?.name||"Oni",
      toUid:targetUid, status:"pending", createdAt:serverTimestamp(),
    });
    await addDoc(collection(db,"users",targetUid,"notifications"), {
      type:"BUDDY_REQUEST", title:`${profile?.displayName} veut être ton Buddy`,
      body:`${oni?.name||"Un Oni"} t'a envoyé une demande.`,
      link:"/social", fromName:profile?.displayName, fromEmoji:oni?.emoji,
      read:false, createdAt:serverTimestamp(),
    });
    setMsg(`Demande envoyée à ${target.displayName||"Oni"} !`);
    setUidInput(""); setResults([]);
  };

  const addByUid = async () => {
    const uid = uidInput.trim().replace(/.*\/u\//, "");
    if (!uid || uid === user.uid) return;
    try {
      const snap = await getDoc(doc(db,"users",uid));
      if (!snap.exists()) { setMsg("Utilisateur introuvable."); return; }
      await sendRequest(snap.data(), uid);
    } catch(e) { setMsg("Erreur."); }
  };

  const accept = async (req) => {
    await addDoc(collection(db,"buddiz"), { users:[user.uid,req.fromUid], createdAt:serverTimestamp() });
    await updateDoc(doc(db,"buddy_requests",req.id), {status:"accepted"});
    await addDoc(collection(db,"users",req.fromUid,"notifications"), {
      type:"BUDDY_ACCEPTED", title:`${profile?.displayName} a accepté ta demande`,
      body:"Vous êtes maintenant Buddiz !", link:"/social",
      fromName:profile?.displayName, fromEmoji:oni?.emoji, read:false, createdAt:serverTimestamp(),
    });
  };

  const decline = async id => updateDoc(doc(db,"buddy_requests",id),{status:"declined"});

  const remove = async (buddy) => {
    if (!window.confirm("Retirer ce Buddy ?")) return;
    await deleteDoc(doc(db,"buddiz",buddy.pairId));
  };

  const challengeDuel = async (buddy) => {
    await addDoc(collection(db,"users",buddy.uid,"notifications"), {
      type:"DUEL_CHALLENGE", title:`${profile?.displayName} te défie en duel !`,
      body:`${oni?.name} t'attend sur /duel`, link:"/duel",
      fromName:profile?.displayName, fromEmoji:oni?.emoji, read:false, createdAt:serverTimestamp(),
    });
    setMsg(`Défi envoyé à ${buddy.displayName} !`);
  };

  return (
    <div className="social-tab">
      {/* Header */}
      <div className="social-tab-header">
        <div>
          <p className="social-tab-title">
            BUDDIZ · {buddies.length}
            {requests.length > 0 && <span className="social-notif-dot">{requests.length}</span>}
          </p>
          {requests.length > 0 && (
            <div className="social-requests">
              {requests.map(req => (
                <div key={req.id} className="social-request-row">
                  <OniAvatar oni={{emoji:req.fromEmoji}} size={36}/>
                  <div style={{flex:1}}>
                    <p className="social-request-name">{req.fromName}</p>
                    <p className="social-request-oni">{req.fromOni}</p>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={()=>accept(req)}>ACCEPTER</button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>decline(req.id)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={()=>setView(v=>v==="add"?"list":"add")}>
          {view==="add" ? "← RETOUR" : "+ AJOUTER"}
        </button>
      </div>

      {msg && <div className="social-msg">{msg}</div>}

      {view === "add" ? (
        /* Add panel */
        <div className="social-add-panel">
          <div className="social-add-section">
            <p className="social-add-label">RECHERCHER</p>
            <div className="social-search-row">
              <input type="text" value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Nom..." onKeyDown={e=>e.key==="Enter"&&doSearch()}/>
              <button className="btn btn-primary btn-sm" onClick={doSearch} disabled={searching}>
                {searching ? <span className="spinner"/> : "→"}
              </button>
            </div>
            {results.map(u => (
              <div key={u.uid} className="social-result-row">
                <OniAvatar oni={u.demon?.oni} size={36}/>
                <div style={{flex:1}}>
                  <p className="social-result-name">{u.displayName||"Oni"}</p>
                  <p className="social-result-oni">{u.demon?.oni?.name||"—"}</p>
                </div>
                <button className="btn btn-primary btn-sm" onClick={()=>sendRequest(u,u.uid)}>+ BUDDY</button>
              </div>
            ))}
          </div>
          <div className="social-add-section">
            <p className="social-add-label">PAR LIEN / UID</p>
            <div className="social-search-row">
              <input type="text" value={uidInput} onChange={e=>setUidInput(e.target.value)}
                placeholder="https://takkyuoni.com/u/... ou UID direct"
                onKeyDown={e=>e.key==="Enter"&&addByUid()}/>
              <button className="btn btn-primary btn-sm" onClick={addByUid}>→</button>
            </div>
          </div>
          <div className="social-add-section">
            <p className="social-add-label">MON LIEN</p>
            <div className="social-my-link">
              <code>{window.location.origin}/u/{user.uid}</code>
              <button className="btn btn-ghost btn-sm" onClick={()=>{
                navigator.clipboard?.writeText(`${window.location.origin}/u/${user.uid}`);
                setMsg("Lien copié !");
              }}>COPIER</button>
            </div>
          </div>
        </div>
      ) : (
        /* Buddies list */
        buddies.length === 0 ? (
          <>
            <EmptyState type="buddiz_empty"/>
            <div style={{textAlign:"center", marginTop:"-1rem"}}>
              <button className="btn btn-primary btn-sm" onClick={()=>setView("add")}>+ AJOUTER UN BUDDY</button>
            </div>
          </>
        ) : (
          <div className="social-buddies-list">
            {buddies.map(b => {
              const lvl = computeLevel(b.xp||0);
              return (
                <div key={b.uid} className="social-buddy-row">
                  <OniAvatar oni={b.demon?.oni} size={52}/>
                  <div className="social-buddy-info">
                    <p className="social-buddy-name">{b.displayName||"Oni"}</p>
                    <p className="social-buddy-oni">{b.demon?.oni?.name||"—"} · {lvl.current.badge} LV.{lvl.current.level}</p>
                    <div className="social-buddy-xp">{b.xp||0} XP</div>
                  </div>
                  <div className="social-buddy-actions">
                    <Link to={`/u/${b.uid}`} className="social-action-btn" title="Voir le profil">👁️</Link>
                    <button className="social-action-btn" title="Défier" onClick={()=>challengeDuel(b)}>⚔️</button>
                    <Link to={`/sessions?invite=${b.uid}`} className="social-action-btn" title="Planifier">📅</Link>
                    <button className="social-action-btn social-action-remove" onClick={()=>remove(b)} title="Retirer">✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SESSIONS TAB
// ══════════════════════════════════════════════════════════════════════
const OBJECTIVE_PRESETS = [
  "Revers","Service","Déplacements","Matchs libres",
  "Multi-balles","Coup droit","Tournoi","Relance","Duel IRL",
];

function SessionsTab({ user, profile }) {
  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filter,   setFilter]   = useState("all");
  const [form, setForm] = useState({title:"",date:"",time:"",objective:"",type:"training",inviteUid:""});
  const oni = profile?.demon?.oni;

  useEffect(() => {
    const q = query(collection(db,"training_sessions"),
      where("memberUids","array-contains",user.uid), orderBy("date","desc"));
    return onSnapshot(q, snap => {
      setSessions(snap.docs.map(d=>({id:d.id,...d.data()})));
      setLoading(false);
    });
  }, [user.uid]);

  const create = async () => {
    if (!form.title||!form.date) return;
    setCreating(true);
    const me = {uid:user.uid, name:profile?.displayName||"Oni", emoji:oni?.emoji||"👹"};
    const participants = [me], memberUids = [user.uid];
    if (form.inviteUid) {
      const snap = await getDoc(doc(db,"users",form.inviteUid.replace(/.*\/u\//,"")));
      if (snap.exists()) {
        const inv = snap.data();
        participants.push({uid:form.inviteUid, name:inv.displayName||"Oni", emoji:inv.demon?.oni?.emoji||"👹"});
        memberUids.push(form.inviteUid);
        await addDoc(collection(db,"users",form.inviteUid,"notifications"), {
          type:"TEAM_INVITE", title:`${profile?.displayName} t'invite à une session`,
          body:`"${form.title}"`, link:"/social", fromName:profile?.displayName, fromEmoji:oni?.emoji,
          read:false, createdAt:serverTimestamp(),
        });
      }
    }
    await addDoc(collection(db,"training_sessions"), {
      title:form.title, date:new Date(form.date), time:form.time||"",
      objective:form.objective||"", type:form.type, status:"planned",
      organizerUid:user.uid, participants, memberUids, notes:[], createdAt:serverTimestamp(),
    });
    setForm({title:"",date:"",time:"",objective:"",type:"training",inviteUid:""});
    setShowForm(false); setCreating(false);
  };

  const updateStatus = async (id,status) => updateDoc(doc(db,"training_sessions",id),{status});
  const addNote = async (session,text) => {
    const note = {text, authorName:profile?.displayName||"Oni", authorUid:user.uid, date:new Date().toISOString()};
    await updateDoc(doc(db,"training_sessions",session.id),{notes:[...(session.notes||[]),note]});
  };
  const del = async id => { if(window.confirm("Supprimer ?")) await deleteDoc(doc(db,"training_sessions",id)); };

  const STATUS = {
    planned:   {label:"PLANIFIÉ",  color:"var(--gold)",  icon:"📅"},
    confirmed: {label:"CONFIRMÉ",  color:"#4a9e6b",      icon:"✓"},
    done:      {label:"TERMINÉ",   color:"var(--ash)",   icon:"🏁"},
    cancelled: {label:"ANNULÉ",    color:"var(--blood)", icon:"✕"},
  };

  const filtered = filter==="all" ? sessions : sessions.filter(s=>s.status===filter);

  return (
    <div className="social-tab">
      <div className="social-tab-header">
        <p className="social-tab-title">SESSIONS · {sessions.length}</p>
        <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(!showForm)}>
          {showForm ? "ANNULER" : "+ PLANIFIER"}
        </button>
      </div>

      {showForm && (
        <div className="social-session-form">
          <div className="social-form-grid">
            <div className="field" style={{gridColumn:"1/-1"}}>
              <label>TITRE</label>
              <input type="text" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Entraînement revers..."/>
            </div>
            <div className="field">
              <label>DATE</label>
              <input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}/>
            </div>
            <div className="field">
              <label>HEURE</label>
              <input type="time" value={form.time} onChange={e=>setForm({...form,time:e.target.value})}/>
            </div>
            <div className="field">
              <label>TYPE</label>
              <select value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                <option value="training">Entraînement</option>
                <option value="duel_irl">Duel IRL ⚔️</option>
                <option value="tournament">Tournoi</option>
                <option value="multiball">Multi-balles</option>
              </select>
            </div>
            <div className="field">
              <label>INVITER (UID)</label>
              <input type="text" value={form.inviteUid} onChange={e=>setForm({...form,inviteUid:e.target.value})} placeholder="UID buddy..."/>
            </div>
            <div className="field" style={{gridColumn:"1/-1"}}>
              <label>OBJECTIF</label>
              <div className="social-obj-chips">
                {OBJECTIVE_PRESETS.map(o => (
                  <button key={o} className={`social-chip${form.objective===o?" active":""}`}
                    onClick={()=>setForm({...form,objective:form.objective===o?"":o})}>{o}</button>
                ))}
              </div>
            </div>
          </div>
          <button className="btn btn-primary" style={{width:"100%",justifyContent:"center"}}
            onClick={create} disabled={creating||!form.title||!form.date}>
            {creating ? <><span className="spinner"/>CRÉATION...</> : "PLANIFIER →"}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="social-filters">
        {["all","planned","confirmed","done"].map(f => (
          <button key={f} className={`social-filter-btn${filter===f?" active":""}`} onClick={()=>setFilter(f)}>
            {f==="all" ? "TOUT" : STATUS[f]?.label}
          </button>
        ))}
      </div>

      {loading ? <div className="social-empty"><span className="spinner"/></div>
      : filtered.length===0 ? (
        <div className="social-empty">
          <span>📅</span><p>Aucune session. Planifie ton prochain entraînement.</p>
        </div>
      ) : (
        <div className="social-sessions-list">
          {filtered.map(s => {
            const st = STATUS[s.status]||STATUS.planned;
            const dateStr = s.date?.toDate?.()
              ? s.date.toDate().toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"})
              : new Date(s.date).toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"});
            return (
              <div key={s.id} className={`social-session-row${s.type==="duel_irl"?" duel-irl":""}`}
                style={{"--sc":st.color}}>
                <div className="social-session-left">
                  <span className="social-session-icon">{st.icon}</span>
                  <div>
                    <p className="social-session-title">{s.title}</p>
                    <p className="social-session-date">{dateStr}{s.time&&` · ${s.time}`}</p>
                    {s.objective && <p className="social-session-obj">{s.objective}</p>}
                    <div className="social-session-participants">
                      {s.participants?.map((p,i)=>(
                        <span key={i} className="social-session-participant">{p.emoji} {p.name}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="social-session-actions">
                  {s.status==="planned" && s.organizerUid===user.uid && (
                    <button className="social-action-btn" onClick={()=>updateStatus(s.id,"confirmed")} title="Confirmer">✓</button>
                  )}
                  {s.status!=="done"&&s.status!=="cancelled" && (
                    <button className="social-action-btn" onClick={()=>updateStatus(s.id,"done")} title="Terminé">🏁</button>
                  )}
                  {s.organizerUid===user.uid && (
                    <button className="social-action-btn social-action-remove" onClick={()=>del(s.id)} title="Supprimer">✕</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// CLANS TAB
// ══════════════════════════════════════════════════════════════════════
function ClanChat({ clanId, profile }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [sending, setSending]   = useState(false);
  const bottomRef = useRef();
  const oni = profile?.demon?.oni;

  useEffect(() => {
    const q = query(collection(db,"clans",clanId,"messages"), orderBy("createdAt","asc"), limit(80));
    return onSnapshot(q, snap => {
      setMessages(snap.docs.map(d=>({id:d.id,...d.data(),createdAt:d.data().createdAt?.toDate?.()||new Date()})));
      setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:"smooth"}),80);
    });
  }, [clanId]);

  const send = async () => {
    if (!input.trim()||sending) return;
    setSending(true);
    await addDoc(collection(db,"clans",clanId,"messages"), {
      text:input.trim(), authorUid:profile?.uid,
      authorName:profile?.displayName||"Oni", authorEmoji:oni?.emoji||"👹",
      authorOni:oni?.name||"Oni", createdAt:serverTimestamp(),
    });
    setInput(""); setSending(false);
  };

  const isMe = msg => msg.authorUid === profile?.uid;

  return (
    <div className="clan-chat-wrap">
      <div className="clan-chat-messages">
        {messages.length===0 && <p className="clan-chat-empty">Aucun message. Brise le silence démoniaque.</p>}
        {messages.map(msg => (
          <div key={msg.id} className={`clan-msg${isMe(msg)?" me":""}`}>
            {!isMe(msg) && (
              <div className="clan-msg-header">
                <span>{msg.authorEmoji}</span>
                <span className="clan-msg-name">{msg.authorName}</span>
                <span className="clan-msg-time">{timeAgo(msg.createdAt)}</span>
              </div>
            )}
            <div className="clan-msg-bubble">
              <p>{msg.text}</p>
              {isMe(msg) && <span className="clan-msg-time right">{timeAgo(msg.createdAt)}</span>}
            </div>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>
      <div className="clan-chat-input">
        <input value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send()}
          placeholder="Message au clan..." maxLength={400}/>
        <button className="btn btn-primary btn-sm" onClick={send} disabled={sending||!input.trim()}>→</button>
      </div>
    </div>
  );
}

function ClansTab({ user, profile }) {
  const navigate = useNavigate();
  const [clans,    setClans]    = useState([]);
  const [myClan,   setMyClan]   = useState(null);
  const [clanView, setClanView] = useState("list"); // list | create | detail
  const [clanId,   setClanId]   = useState(null);
  const [clanData, setClanData] = useState(null);
  const [clanTab,  setClanTab]  = useState("chat"); // chat | members
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({name:"",tag:"",description:"",emoji:"👹",maxMembers:20});
  const oni = profile?.demon?.oni;

  useEffect(() => {
    const q = query(collection(db,"clans"), orderBy("totalXp","desc"), limit(20));
    return onSnapshot(q, snap => {
      const all = snap.docs.map(d=>({id:d.id,...d.data()}));
      setClans(all);
      if (profile?.clanId) {
        const mine = all.find(c=>c.id===profile.clanId);
        if (mine) { setMyClan(mine); setClanId(mine.id); setClanData(mine); }
      }
    });
  }, [profile?.clanId]);

  // Listen to clan updates if in one
  useEffect(() => {
    if (!clanId) return;
    return onSnapshot(doc(db,"clans",clanId), snap => {
      if (snap.exists()) setClanData({id:snap.id,...snap.data()});
    });
  }, [clanId]);

  const join = async (clan) => {
    if (profile?.clanId) return;
    const me = {uid:user.uid, displayName:profile?.displayName||"Oni",
      oniName:oni?.name||"Oni", oniEmoji:oni?.emoji||"👹", xp:profile?.xp||0, joinedAt:new Date().toISOString()};
    await updateDoc(doc(db,"clans",clan.id), {members:arrayUnion(me), memberUids:arrayUnion(user.uid)});
    await updateDoc(doc(db,"users",user.uid), {clanId:clan.id});
    setClanId(clan.id); setClanView("detail");
  };

  const leave = async () => {
    if (!myClan||!window.confirm("Quitter ce clan ?")) return;
    await updateDoc(doc(db,"clans",myClan.id),{
      members:myClan.members.filter(m=>m.uid!==user.uid), memberUids:arrayRemove(user.uid)
    });
    await updateDoc(doc(db,"users",user.uid),{clanId:null});
    setMyClan(null); setClanId(null); setClanData(null); setClanView("list");
  };

  const createClan = async () => {
    if (!form.name.trim()||!form.tag.trim()||profile?.clanId) return;
    setCreating(true);
    const me = {uid:user.uid, displayName:profile?.displayName||"Oni",
      oniName:oni?.name||"Oni", oniEmoji:oni?.emoji||"👹", xp:profile?.xp||0, joinedAt:new Date().toISOString()};
    const ref = await addDoc(collection(db,"clans"), {
      name:form.name.trim(), tag:`[${form.tag.trim().toUpperCase().slice(0,4)}]`,
      description:form.description.trim(), emoji:form.emoji,
      maxMembers:Number(form.maxMembers), leaderUid:user.uid,
      members:[me], memberUids:[user.uid], totalXp:profile?.xp||0, createdAt:serverTimestamp(),
    });
    await updateDoc(doc(db,"users",user.uid),{clanId:ref.id});
    setClanId(ref.id); setClanView("detail"); setCreating(false);
  };

  // ── Vue détail clan ──
  if (clanView==="detail"&&clanData) {
    const isMember = clanData.memberUids?.includes(user.uid);
    const isLeader = clanData.leaderUid===user.uid;
    const totalXp  = clanData.members?.reduce((s,m)=>s+(m.xp||0),0)||0;
    return (
      <div className="social-tab">
        <div className="social-tab-header">
          <button className="btn btn-ghost btn-sm" onClick={()=>setClanView("list")}>← CLANS</button>
          {isMember&&!isLeader && <button className="btn btn-ghost btn-sm" onClick={leave}>QUITTER</button>}
          {!isMember && !profile?.clanId && (
            <button className="btn btn-primary btn-sm" onClick={()=>join(clanData)}>REJOINDRE →</button>
          )}
        </div>

        <div className="clan-detail-header">
          <span className="clan-detail-emoji">{clanData.emoji}</span>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
              <span className="clan-detail-tag">{clanData.tag}</span>
              <span className="clan-detail-name">{clanData.name}</span>
            </div>
            <p className="clan-detail-desc">{clanData.description}</p>
          </div>
          <div className="clan-detail-stats">
            <div className="clan-stat"><span className="clan-stat-val">{clanData.memberUids?.length||0}</span><span className="clan-stat-label">membres</span></div>
            <div className="clan-stat"><span className="clan-stat-val">{totalXp.toLocaleString()}</span><span className="clan-stat-label">XP total</span></div>
          </div>
        </div>

        {isMember && (
          <div className="clan-inner-tabs">
            <button className={`social-filter-btn${clanTab==="chat"?" active":""}`} onClick={()=>setClanTab("chat")}>💬 CHAT</button>
            <button className={`social-filter-btn${clanTab==="members"?" active":""}`} onClick={()=>setClanTab("members")}>👹 MEMBRES</button>
          </div>
        )}

        {clanTab==="chat"&&isMember && <ClanChat clanId={clanData.id} profile={{...profile,uid:user.uid}}/>}
        {!isMember && <div className="social-empty"><span>🔒</span><p>Rejoins le clan pour accéder au chat.</p></div>}

        {clanTab==="members" && (
          <div className="social-sessions-list">
            {(clanData.members||[]).sort((a,b)=>(b.xp||0)-(a.xp||0)).map((m,i) => {
              const lvl = computeLevel(m.xp||0);
              return (
                <div key={m.uid} className="social-buddy-row">
                  <span className="social-member-rank">#{i+1}</span>
                  <span style={{fontSize:"1.4rem"}}>{m.oniEmoji||"👹"}</span>
                  <div className="social-buddy-info">
                    <p className="social-buddy-name">
                      {m.displayName}
                      {m.uid===clanData.leaderUid&&<span style={{color:"var(--gold)"}}> 👑</span>}
                    </p>
                    <p className="social-buddy-oni">{m.oniName} · {lvl.current.badge} LV.{lvl.current.level}</p>
                  </div>
                  <span style={{fontFamily:"var(--font-disp)",color:"var(--gold)",fontSize:".85rem"}}>{(m.xp||0).toLocaleString()} XP</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Vue créer clan ──
  if (clanView==="create") return (
    <div className="social-tab">
      <div className="social-tab-header">
        <p className="social-tab-title">FONDER UN CLAN</p>
        <button className="btn btn-ghost btn-sm" onClick={()=>setClanView("list")}>ANNULER</button>
      </div>
      <div className="social-form-grid">
        <div className="field"><label>EMOJI</label><input type="text" value={form.emoji} onChange={e=>setForm({...form,emoji:e.target.value})} maxLength={2}/></div>
        <div className="field"><label>TAG (4 max)</label><input type="text" value={form.tag} onChange={e=>setForm({...form,tag:e.target.value})} maxLength={4} placeholder="ONI"/></div>
        <div className="field" style={{gridColumn:"1/-1"}}><label>NOM</label><input type="text" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Les Démons du Filet..."/></div>
        <div className="field" style={{gridColumn:"1/-1"}}><label>DESCRIPTION</label><textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={2}/></div>
        <div className="field"><label>MAX MEMBRES</label><select value={form.maxMembers} onChange={e=>setForm({...form,maxMembers:e.target.value})}><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option></select></div>
      </div>
      <button className="btn btn-primary" style={{width:"100%",justifyContent:"center"}} onClick={createClan} disabled={creating||!form.name||!form.tag}>
        {creating ? <><span className="spinner"/>FONDATION...</> : "FONDER →"}
      </button>
    </div>
  );

  // ── Vue liste clans ──
  return (
    <div className="social-tab">
      <div className="social-tab-header">
        <p className="social-tab-title">CLANS · {clans.length}</p>
        {!profile?.clanId && (
          <button className="btn btn-primary btn-sm" onClick={()=>setClanView("create")}>+ CRÉER</button>
        )}
        {myClan && (
          <button className="btn btn-ghost btn-sm" onClick={()=>setClanView("detail")}>
            {myClan.emoji} MON CLAN →
          </button>
        )}
      </div>

      {clans.length===0 ? (
        <EmptyState type="clans_empty"/>
      ) : (
        <div className="social-clans-list">
          {clans.map((clan,i) => (
            <button key={clan.id} className={`social-clan-row${clan.id===profile?.clanId?" mine":""}`}
              onClick={()=>{setClanId(clan.id);setClanData(clan);setClanView("detail");}}>
              <span className="social-clan-rank">#{i+1}</span>
              <span className="social-clan-emoji">{clan.emoji}</span>
              <div className="social-clan-info">
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span className="social-clan-tag">{clan.tag}</span>
                  <span className="social-clan-name">{clan.name}</span>
                  {clan.id===profile?.clanId&&<span className="badge badge-blood">MON CLAN</span>}
                </div>
                <p className="social-clan-desc">{clan.description}</p>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <p style={{fontFamily:"var(--font-disp)",color:"var(--gold)",fontSize:".9rem"}}>{(clan.totalXp||0).toLocaleString()}</p>
                <p style={{fontSize:".6rem",color:"var(--ash)",letterSpacing:"2px"}}>{clan.memberUids?.length||0}/{clan.maxMembers} ONI</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════
export default function SocialPage() {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState("buddiz");

  if (!user) return (
    <div className="social-page section" style={{paddingTop:"8rem",textAlign:"center"}}>
      <span className="section-eyebrow">仲間 · Social</span>
      <h2 className="section-title">Connexion requise</h2>
      <div className="divider divider-center"/>
      <Link to="/auth" className="btn btn-primary" style={{marginTop:"1.5rem"}}>ENTRER →</Link>
    </div>
  );

  const oni = profile?.demon?.oni;

  return (
    <div className="social-page">
      <div className="section" style={{paddingTop:"7rem"}}>

        {/* Header compact */}
        <div className="social-header">
          <div>
            <span className="section-eyebrow">仲間 · Social</span>
            <h1 className="section-title">Espace Social</h1>
            <div className="divider"/>
          </div>
          {oni && (
            <div className="social-my-oni">
              <OniAvatar oni={oni} size={52}/>
              <div>
                <p className="social-my-name">{profile?.displayName||"Oni"}</p>
                <p className="social-my-oni-name">{oni.jp} · {oni.name}</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="social-main-tabs">
          {[
            ["buddiz",   "🤝 BUDDIZ"],
            ["sessions", "📅 SESSIONS"],
            ["clans",    "一 CLANS"],
          ].map(([id,label]) => (
            <button key={id} className={`social-main-tab${tab===id?" active":""}`} onClick={()=>setTab(id)}>
              {label}
            </button>
          ))}
        </div>

        <div className="social-content">
          {tab==="buddiz"  && <BuddizTab   user={user} profile={{...profile,uid:user.uid}}/>}
          {tab==="sessions"&& <SessionsTab user={user} profile={{...profile,uid:user.uid}}/>}
          {tab==="clans"   && <ClansTab    user={user} profile={{...profile,uid:user.uid}}/>}
        </div>
      </div>
    </div>
  );
}
